import { Injectable } from '@angular/core';
import { TenantService } from '@vsolv/core/multi-tenant/web';
import { combineLatest, map, Observable } from 'rxjs';
import { FeatureFlagsService } from '../../policy/services/feature-flags.service';
import {
  getTenantFeatureFlagName,
  isLegacyTenantFeatureEnabled,
  TenantFeature,
} from '../config/tenant-features.config';

/**
 * Service to check tenant-specific feature flags
 *
 * @example
 * constructor(private tenantFeatureSvc: TenantFeatureService) {}
 *
 * canShowDeclaration$ = this.tenantFeatureSvc.isFeatureEnabled(TenantFeature.DECLARATION_PAGE);
 */
@Injectable({ providedIn: 'root' })
export class TenantFeatureService {
  constructor(
    private tenantSvc: TenantService,
    private featureFlagsSvc: FeatureFlagsService
  ) {}

  /**
   * Check if current tenant has access to a specific feature
   */
  isFeatureEnabled(feature: TenantFeature): Observable<boolean> {
    const flagName = getTenantFeatureFlagName(feature);

    return combineLatest([this.tenantSvc.tenantId$, this.featureFlagsSvc.getAll()]).pipe(
      map(([tenantId, featureFlags]) => {
        if (Object.prototype.hasOwnProperty.call(featureFlags, flagName)) {
          return Boolean(featureFlags[flagName]);
        }

        return isLegacyTenantFeatureEnabled(tenantId, feature);
      })
    );
  }

  isTenantFeatureEnabled(tenantId: string | undefined, feature: TenantFeature): boolean {
    return isLegacyTenantFeatureEnabled(tenantId, feature);
  }
}
